// @systeme: interface
// Un éditeur riche léger, sans dépendance : le texte se tape tel quel, avec trois
// conventions simples que l'affichage comprend. Une ligne qui commence par « - » est une
// puce ; « 1. » une étape numérotée ; « **mot** » un mot en gras ; « # » ou « ## » un
// titre (pour les textes légaux). Rien d'autre.
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';

/** Découpe « du texte en **gras** » en morceaux, les gras en <strong>. */
function avecGras(texte: string): ReactNode[] {
  const morceaux: ReactNode[] = [];
  const motif = /\*\*([^*]+)\*\*/g;
  let dernier = 0;
  let m: RegExpExecArray | null;
  while ((m = motif.exec(texte)) !== null) {
    if (m.index > dernier) morceaux.push(texte.slice(dernier, m.index));
    morceaux.push(<strong key={m.index}>{m[1]}</strong>);
    dernier = m.index + m[0].length;
  }
  if (dernier < texte.length) morceaux.push(texte.slice(dernier));
  return morceaux;
}

type Bloc =
  | { type: 'paragraphe'; lignes: string[] }
  | { type: 'puces'; lignes: string[] }
  | { type: 'numeros'; lignes: string[] }
  | { type: 'titre'; niveau: 1 | 2; lignes: string[] }
  | { type: 'citation'; lignes: string[] };

/** Regroupe les lignes en paragraphes, listes à puces et listes numérotées. */
export function decouper(texte: string): Bloc[] {
  const blocs: Bloc[] = [];
  for (const brute of texte.replace(/\r\n/g, '\n').split('\n')) {
    const ligne = brute.trimEnd();
    const dernier = blocs[blocs.length - 1];
    if (ligne.trim() === '') {
      if (dernier && dernier.type === 'paragraphe' && dernier.lignes.length > 0)
        blocs.push({ type: 'paragraphe', lignes: [] });
      continue;
    }
    const puce = /^\s*[-•*]\s+(.*)$/.exec(ligne);
    const numero = /^\s*\d+[.)]\s+(.*)$/.exec(ligne);
    const titre = /^(#{1,2})\s+(.*)$/.exec(ligne);
    const citation = /^>\s?(.*)$/.exec(ligne);
    if (titre) {
      blocs.push({ type: 'titre', niveau: titre[1] === '#' ? 1 : 2, lignes: [titre[2] ?? ''] });
    } else if (citation) {
      if (dernier?.type === 'citation') dernier.lignes.push(citation[1] ?? '');
      else blocs.push({ type: 'citation', lignes: [citation[1] ?? ''] });
    } else if (puce) {
      if (dernier?.type === 'puces') dernier.lignes.push(puce[1] ?? '');
      else blocs.push({ type: 'puces', lignes: [puce[1] ?? ''] });
    } else if (numero) {
      if (dernier?.type === 'numeros') dernier.lignes.push(numero[1] ?? '');
      else blocs.push({ type: 'numeros', lignes: [numero[1] ?? ''] });
    } else if (dernier?.type === 'paragraphe') {
      dernier.lignes.push(ligne);
    } else {
      blocs.push({ type: 'paragraphe', lignes: [ligne] });
    }
  }
  return blocs.filter((b) => b.lignes.length > 0);
}

export function TexteRiche({ texte }: { texte: string }) {
  return (
    <Box sx={{ '& ul, & ol': { pl: 3, my: 0.5 }, '& li': { mb: 0.25 } }}>
      {decouper(texte).map((bloc, i) => {
        if (bloc.type === 'titre')
          return (
            <Typography
              key={i}
              variant={bloc.niveau === 1 ? 'h2' : 'h3'}
              component={bloc.niveau === 1 ? 'h2' : 'h3'}
              sx={{ mt: 2.5, mb: 1 }}
            >
              {avecGras(bloc.lignes[0] ?? '')}
            </Typography>
          );
        if (bloc.type === 'citation')
          return (
            <Typography
              key={i}
              color="text.secondary"
              sx={{ mb: 1, pl: 1.5, borderLeft: 3, borderColor: 'divider' }}
            >
              {avecGras(bloc.lignes.join(' '))}
            </Typography>
          );
        if (bloc.type === 'paragraphe')
          return (
            <Typography key={i} sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
              {bloc.lignes.map((l, j) => (
                <span key={j}>
                  {j > 0 && <br />}
                  {avecGras(l)}
                </span>
              ))}
            </Typography>
          );
        const Liste = bloc.type === 'puces' ? 'ul' : 'ol';
        return (
          <Liste key={i}>
            {bloc.lignes.map((l, j) => (
              <li key={j}>
                <Typography component="span">{avecGras(l)}</Typography>
              </li>
            ))}
          </Liste>
        );
      })}
    </Box>
  );
}
